import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  signal,
  untracked
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Color, PieChartModule, ScaleType } from '@swimlane/ngx-charts';
import { Subscription } from 'rxjs';
import { CalendarEventStatusName } from 'src/app/models/calendar-event.interface';
import { CalendarEventStatusCounts } from 'src/app/models/calendar-events-response.interface';
import { CalendarParams } from 'src/app/models/calendar-params.interface';
import { Profile } from 'src/app/models/profile.model';
import { SettlementsService } from 'src/app/services/settlements.service';
import { IconsService } from 'src/app/shared/services/icons.service';
import {
  CalendarSummaryPeriod,
  formatCalendarApiDate,
  getCalendarSummaryDateRange
} from 'src/app/shared/utils/calendar-summary-period.utils';

interface CalendarEventStatusPresentation {
  name: CalendarEventStatusName;
  label: string;
  color: string;
}

type CalendarClientType = 0 | 1 | null;

const CALENDAR_EVENT_STATUS_PRESENTATION: CalendarEventStatusPresentation[] = [
  { name: 'PENDING', label: 'Pendientes', color: '#d3a913' },
  { name: 'OVERDUE', label: 'Atrasadas', color: '#d9534f' },
  { name: 'IN_PROGRESS', label: 'En proceso', color: '#2f80ed' },
  { name: 'COMPLETED', label: 'Completadas', color: '#27ae60' },
  { name: 'CANCELED', label: 'Canceladas', color: '#9ca3af' }
];

const EMPTY_CALENDAR_EVENT_STATUS_COUNTS: CalendarEventStatusCounts = {
  COMPLETED: 0,
  IN_PROGRESS: 0,
  CANCELED: 0,
  PENDING: 0,
  OVERDUE: 0
};

@Component({
  selector: 'app-calendar-task-summary',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, FontAwesomeModule, PieChartModule],
  templateUrl: './calendar-task-summary.component.html',
  styleUrl: './calendar-task-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarTaskSummaryComponent implements OnDestroy {
  readonly currentProfile = input.required<Profile>();

  private readonly store = inject(SettlementsService);
  private summarySubscription?: Subscription;
  private summaryRequestId = 0;

  readonly icons = inject(IconsService);
  readonly isLoading = signal(false);
  readonly hasError = signal(false);
  readonly period = signal<CalendarSummaryPeriod>('weekly');
  readonly clientType = signal<CalendarClientType>(null);
  readonly rangeLabel = signal('');
  readonly counts = signal<CalendarEventStatusCounts>({ ...EMPTY_CALENDAR_EVENT_STATUS_COUNTS });
  readonly periodOptions: { value: CalendarSummaryPeriod; label: string }[] = [
    { value: 'weekly', label: 'Semanal' },
    { value: 'fortnightly', label: 'Quincenal' },
    { value: 'monthly', label: 'Mensual' }
  ];
  readonly clientTypeOptions: { value: CalendarClientType; label: string }[] = [
    { value: null, label: 'Todos' },
    { value: 0, label: 'Habitacional' },
    { value: 1, label: 'Comercial' }
  ];
  readonly colorScheme: Color = {
    name: 'calendar-event-statuses',
    selectable: false,
    group: ScaleType.Ordinal,
    domain: CALENDAR_EVENT_STATUS_PRESENTATION.map(status => status.color)
  };
  readonly data = computed(() => CALENDAR_EVENT_STATUS_PRESENTATION.map(status => ({
    name: status.label,
    value: this.counts()[status.name]
  })));
  readonly legend = computed(() => CALENDAR_EVENT_STATUS_PRESENTATION.map(status => ({
    ...status,
    value: this.counts()[status.name]
  })));
  readonly total = computed(() => this.data().reduce((sum, item) => sum + item.value, 0));

  constructor() {
    effect(() => {
      const utilityId = this.getActiveUtilityId();
      untracked(() => this.loadTaskSummary(utilityId));
    });
  }

  ngOnDestroy(): void {
    this.summarySubscription?.unsubscribe();
  }

  changePeriod(period: CalendarSummaryPeriod): void {
    this.period.set(period);
    this.loadTaskSummary();
  }

  changeClientType(clientType: CalendarClientType): void {
    this.clientType.set(clientType);
    this.loadTaskSummary();
  }

  loadTaskSummary(utilityId: number | null = this.getActiveUtilityId()): void {
    const requestId = ++this.summaryRequestId;
    this.summarySubscription?.unsubscribe();
    this.summarySubscription = undefined;

    if (!utilityId) {
      this.resetSummary();
      return;
    }

    const range = getCalendarSummaryDateRange(new Date(), this.period());
    const params: Partial<CalendarParams> = {
      start_date: formatCalendarApiDate(range.startDate),
      end_date: formatCalendarApiDate(range.endDate),
      utility_id: utilityId
    };
    const clientType = this.clientType();
    if (clientType === 0 || clientType === 1) {
      params.settlement_tipo = clientType;
    }

    this.rangeLabel.set(this.formatRangeLabel(range.startDate, range.endDate));
    this.counts.set({ ...EMPTY_CALENDAR_EVENT_STATUS_COUNTS });
    this.isLoading.set(true);
    this.hasError.set(false);

    this.summarySubscription = this.store.getCalendarEvents(params).subscribe({
      next: ({ counts }) => {
        if (!this.isActiveRequest(requestId, utilityId)) return;
        this.counts.set(counts);
        this.isLoading.set(false);
      },
      error: () => {
        if (!this.isActiveRequest(requestId, utilityId)) return;
        this.counts.set({ ...EMPTY_CALENDAR_EVENT_STATUS_COUNTS });
        this.hasError.set(true);
        this.isLoading.set(false);
      }
    });
  }

  private getActiveUtilityId(): number | null {
    const utilityId = Number(this.currentProfile()?.utility_id);
    return Number.isFinite(utilityId) && utilityId > 0 ? utilityId : null;
  }

  private isActiveRequest(requestId: number, utilityId: number): boolean {
    return requestId === this.summaryRequestId && utilityId === this.getActiveUtilityId();
  }

  private resetSummary(): void {
    this.counts.set({ ...EMPTY_CALENDAR_EVENT_STATUS_COUNTS });
    this.rangeLabel.set('');
    this.hasError.set(false);
    this.isLoading.set(false);
  }

  private formatRangeLabel(startDate: Date, endDate: Date): string {
    const formatter = new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    return `${formatter.format(startDate)} – ${formatter.format(endDate)}`;
  }
}
